/* eslint-disable react/jsx-max-props-per-line */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { Grid, Typography } from '@mui/material';
import moment from 'moment';
import validate from 'validate.js';
import {
  DialogDefaultV2,
  InputV2,
  DateTimePickerV2,
  ArquivoContainerV2,
} from '../../../../components/v2';

import * as selectorsAuth from '../../../../store/auth/reducer';
import * as selectorsNsir from '../../../../store/nsir/reducer';
import * as selectorsNsmp from '../../../../store/nsmp/reducer';
import * as actionsNsmp from '../../../../store/nsmp/actions';
import * as Role from '../../../../constants/role';
import { isInspecaoAnticorrocao, isInspecaoMtBt } from 'utils';

const rootGridSx = { pt: 1 };
const fieldContainerSx = { pb: 0 };
const requiredTextSx = { color: 'error.main', pt: 0.5 };

const NsmpAnexoModal = ({
  open,
  onCancel,
  onConfirm,
  selectedId,
  nsmpSelected,
  loadNsmpById,
  readOnly,
  userRole,
  nsirData,
  nsirTitle,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
  carouselPosition,
}) => {
  const [canEditNsmp, setCanEditNsmp] = useState(false);
  const [readOnlyLocal, setReadOnlyLocal] = useState(readOnly);
  const [editFiles, setEditFiles] = useState(false);
  const [errors, setErrors] = useState({});
  const [values, setValues] = useState({
    dataFotoDrone: moment().valueOf(),
    fotos: [],
  });

  useEffect(() => {
    setReadOnlyLocal(readOnly);
  }, [readOnly]);

  useEffect(() => {
    if (selectedId) loadNsmpById(selectedId);
  }, [selectedId, loadNsmpById]);

  useEffect(() => {
    if (nsmpSelected && nsmpSelected.id) {
      setValues(nsmpSelected);
    }
  }, [nsmpSelected]);

  const handleChange = (id, value) => {
    setValues({
      ...values,
      [id]: value,
    });
  };

  const clearForm = () => {
    setCanEditNsmp(false);
    setEditFiles(false);
    setErrors({});
    setValues({
      fotos: [],
      dataFotoDrone: moment().valueOf(),
    });
  };

  const revertToViewMode = () => {
    setCanEditNsmp(false);
    setEditFiles(false);
    setErrors({});
    setReadOnlyLocal(true);
    if (nsmpSelected && nsmpSelected.id) {
      setValues(nsmpSelected);
    }
  };

  const onCancelLocal = () => {
    if (canEditNsmp) {
      revertToViewMode();
      return;
    }
    clearForm();
    onCancel();
  };

  const getDataFotoDrone = () => {
    let data = moment().valueOf();
    if (values.dataFotoDrone) {
      data = values.dataFotoDrone;
    }
    return moment(data).valueOf();
  };

  const buildPayload = () => ({
    ...values,
    dataFotoDrone: getDataFotoDrone(),
    updateFiles: editFiles,
  });

  const onConfirmLocal = () => {
    const validationErrors = validate(values, {
      fotos: {
        presence: { allowEmpty: false, message: 'é obrigatório' },
      },
    });
    setErrors(validationErrors);
    if (!validationErrors || Object.keys(validationErrors).length === 0) {
      const payload = buildPayload();
      clearForm();
      onConfirm(payload);
    }
  };

  const isAdmin = () => userRole === Role.ADMIN || userRole === Role.SUPER_ADMIN;

  const getTitle = () => {
    if (values && values.fotoInicio) {
      return `${nsirTitle} > Foto Inicial`;
    }
    if (values && values.fotoFim) {
      return `${nsirTitle} > Foto Final`;
    }
    if (values && values.analiseRisco) {
      return `${nsirTitle} > Análise de Risco`;
    }
    if (values && values.fotoRegistro) {
      return `${nsirTitle} > Foto Registro ${values.codigo}`;
    }
    return `${nsirTitle} > Anexo`;
  };

  const isFotoRegistro = () => values.fotoRegistro === true;

  const buildHeaderActions = () => {
    const actions = [];
    if (carouselPosition) {
      actions.push({ title: '◄', onClick: onPrevious, disabled: !hasPrevious });
      actions.push({
        title: `${carouselPosition.current} / ${carouselPosition.total}`,
        onClick: () => {},
        disabled: true,
      });
      actions.push({ title: '►', onClick: onNext, disabled: !hasNext });
    }

    if (isAdmin() && readOnlyLocal) {
      actions.push({
        onClick: () => {
          setReadOnlyLocal(false);
          setCanEditNsmp(true);
        },
        title: 'Editar',
      });
    }

    return actions.length > 0 ? actions : null;
  };

  const isReadOnly = canEditNsmp ? false : readOnlyLocal;

  const inspecionadoEmValue = () => {
    if (values.inspecionadoEm == null || values.inspecionadoEm === '') return null;
    const parsed = moment(values.inspecionadoEm);
    return parsed.isValid() ? parsed.valueOf() : null;
  };

  const showDataInspecao = isInspecaoMtBt(nsirData?.tipoServicoId);

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onCancelLocal}
      handlerConfirm={onConfirmLocal}
      title={getTitle()}
      confirmMessage="Salvar"
      maxWidth="lg"
      fullWidth
      readOnly={isReadOnly}
      headerAction={buildHeaderActions()}
    >
      <Grid container spacing={1} sx={rootGridSx}>
        {showDataInspecao && (
          <Grid size={{ xs: 12, sm: 4 }} sx={fieldContainerSx}>
            <DateTimePickerV2
              label="Data da inspeção"
              value={inspecionadoEmValue()}
              onChange={() => {}}
              disabled
            />
          </Grid>
        )}
        {isFotoRegistro() && (
          <Grid size={{ xs: 12 }} sx={fieldContainerSx}>
            <InputV2
              label="Detalhes"
              name="observacao"
              onChange={event => handleChange(event.target.name, event.target.value)}
              multiline
              rows={4}
              value={values.observacao}
              disabled={readOnlyLocal && !canEditNsmp}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }} sx={fieldContainerSx}>
          <ArquivoContainerV2
            arquivos={values.fotos}
            showDescription={isInspecaoAnticorrocao(nsirData.tipoServicoId)}
            onSave={fotos => {
              setEditFiles(true);
              setValues({ ...values, fotos });
            }}
            disabled={canEditNsmp ? false : readOnlyLocal}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={fieldContainerSx}>
          {errors && errors.fotos && (
            <Typography sx={requiredTextSx}>*Imagens são obrigatórias</Typography>
          )}
        </Grid>
      </Grid>
    </DialogDefaultV2>
  );
};

NsmpAnexoModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  selectedId: PropTypes.string,
  nsmpSelected: PropTypes.object.isRequired,
  loadNsmpById: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  nsirData: PropTypes.object.isRequired,
  nsirTitle: PropTypes.string.isRequired,
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
  hasNext: PropTypes.bool,
  hasPrevious: PropTypes.bool,
  carouselPosition: PropTypes.object,
};

NsmpAnexoModal.defaultProps = {
  selectedId: undefined,
};

const mapStateToProps = state => ({
  nsmpSelected: selectorsNsmp.getNsmpSelected(state),
  nsirData: selectorsNsir.getNsirSelected(state),
  userRole: selectorsAuth.getRole(state),
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      ...actionsNsmp,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(NsmpAnexoModal);
